import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { io as socketIO } from 'socket.io-client';

const DOC_TYPES = [
  { value: 'invoice', label: '📄 Upload Invoice' },
  { value: 'packing_list', label: '📦 Packing List' },
  { value: 'shipping_doc', label: '🚢 Shipping Doc' },
  { value: 'bill_of_lading', label: '📋 Bill of Lading' },
  { value: 'other', label: '📎 Other' },
];

export default function VendorOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const socketRef = useRef();
  const msgEndRef = useRef();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('ts_user'));
    if (!user || user.role !== 'vendor') { navigate('/vendor/login'); return; }

    axios.get('/api/vendor/orders').then(res => {
      const all = Array.isArray(res.data) ? res.data : (res.data.data || []);
      const o = all.find(x => x.id === id);
      if (!o) { navigate('/vendor/dashboard'); return; }
      setOrder(o);
    });

    fetchMessages();
    fetchDocuments();

    const token = localStorage.getItem('ts_token');
    socketRef.current = socketIO(window.location.origin, { auth: { token } });
    socketRef.current.on('connect', () => socketRef.current.emit('join_order', id));
    socketRef.current.on('new_message', (msg) => setMessages(prev => [...prev, msg.message || msg]));
    socketRef.current.on('new_document', () => fetchDocuments());
    socketRef.current.on('status_update', (data) => setOrder(prev => prev ? { ...prev, status: data.status || data.new_status } : prev));
    socketRef.current.on('document_status', ({ docId, status }) => setDocuments(prev => prev.map(d => d.id === docId ? { ...d, status } : d)));

    return () => socketRef.current?.close();
  }, [id]);

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`/api/vendor/orders/${id}/messages`);
      setMessages(Array.isArray(res.data) ? res.data : (res.data.data || []));
    } catch {}
  };

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`/api/vendor/orders/${id}/documents`);
      setDocuments(Array.isArray(res.data) ? res.data : (res.data.data || []));
    } catch {}
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    await axios.post(`/api/vendor/orders/${id}/messages`, { content: newMessage });
    setNewMessage('');
    await fetchMessages();
  };

  const uploadDocument = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    try {
      await axios.post(`/api/vendor/orders/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await fetchDocuments();
    } catch (err) {
      alert('Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  if (!order) return <div className="loading-screen">Loading...</div>;

  const vendorDocs = documents.filter(d => d.uploaded_by === 'vendor');
  const buyerDocs = documents.filter(d => d.uploaded_by === 'buyer');

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-50)' }}>
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-logo">TradeSync</div>
            <nav className="header-nav">
              <Link to="/vendor/dashboard">My Orders</Link>
            </nav>
          </div>
          <div className="header-right">
            <span style={{ color: 'var(--gray-500)', fontSize: 14 }}>{order.order_number}</span>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
        <Link to="/vendor/dashboard" style={{ color: 'var(--gray-500)', textDecoration: 'none', fontSize: 14 }}>← Back to Orders</Link>

        <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: 24, marginTop: 16, height: 'calc(100vh - 160px)' }}>
          {/* Left sidebar: Status + Uploads */}
          <div style={{ background: 'var(--white)', borderRadius: 12, padding: 20, overflow: 'auto', border: '1px solid var(--gray-200)' }}>
            {/* Status */}
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ margin: '0 0 8px 0' }}>Current Status</h4>
              <div style={{
                fontSize: '1.1em', fontWeight: 700, padding: 12, borderRadius: 8, textAlign: 'center',
                background: order.status === 'draft' ? 'var(--warning-light)' : order.status === 'shipped' ? 'var(--success-light)' : 'var(--info-light)',
                color: order.status === 'draft' ? 'var(--warning)' : order.status === 'shipped' ? 'var(--success)' : 'var(--info)'
              }}>
                {order.status.replace(/_/g, ' ').toUpperCase()}
              </div>
              {order.status === 'draft' && <p style={{ fontSize: 13, color: 'var(--gray-400)', textAlign: 'center', marginTop: 8 }}>Awaiting buyer deposit confirmation</p>}
            </div>

            {/* Upload */}
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ margin: '0 0 12px 0' }}>Upload Documents</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {DOC_TYPES.map(dt => (
                  <label key={dt.value} style={{
                    background: 'var(--gray-50)', border: '2px dashed var(--gray-200)', padding: 14, textAlign: 'center',
                    borderRadius: 8, cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.5 : 1, transition: 'all 0.2s'
                  }}>
                    {dt.label}
                    <input type="file" style={{ display: 'none' }} onChange={(e) => uploadDocument(e, dt.value)} accept=".pdf,.jpg,.jpeg,.png,.xlsx,.doc,.docx" />
                  </label>
                ))}
              </div>
            </div>

            {/* Order info */}
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ margin: '0 0 8px 0' }}>Order Details</h4>
              <div style={{ fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                  <span style={{ color: 'var(--gray-500)' }}>Amount</span><strong>{formatCurrency(order.total_amount)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--gray-100)' }}>
                  <span style={{ color: 'var(--gray-500)' }}>From</span><span>{order.port_departure || '—'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                  <span style={{ color: 'var(--gray-500)' }}>To</span><span>{order.port_arrival || '—'}</span>
                </div>
              </div>
            </div>

            {/* My uploads */}
            {vendorDocs.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 8px 0' }}>Your Uploads</h4>
                {vendorDocs.map(doc => (
                  <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 }}>
                    <span>{(doc.doc_type || 'other').replace(/_/g, ' ')} v{doc.version}</span>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                      background: doc.status === 'approved' ? 'var(--success-light)' : doc.status === 'rejected' ? 'var(--danger-light)' : 'var(--warning-light)',
                      color: doc.status === 'approved' ? 'var(--success)' : doc.status === 'rejected' ? 'var(--danger)' : 'var(--warning)'
                    }}>{doc.status}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Buyer docs */}
            {buyerDocs.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--gray-200)' }}>
                <h4 style={{ margin: '0 0 8px 0' }}>Buyer Documents</h4>
                {buyerDocs.map(doc => (
                  <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', fontSize: 13 }}>
                    <span>{(doc.doc_type || 'other').replace(/_/g, ' ')} v{doc.version}</span>
                    <a href={`/api/documents/${doc.id}/download?token=${localStorage.getItem('ts_token')}`} style={{ color: 'var(--primary)', fontSize: 12 }} target="_blank" rel="noopener">Download</a>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Chat */}
          <div style={{ background: 'var(--white)', borderRadius: 12, border: '1px solid var(--gray-200)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--gray-200)' }}>
              <h4 style={{ margin: 0 }}>💬 Chat with Buyer</h4>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--gray-400)', padding: 40 }}>No messages yet. Start the conversation below.</div>
              ) : messages.map(msg => (
                <div key={msg.id} style={{
                  alignSelf: msg.sender_type === 'vendor' ? 'flex-end' : 'flex-start',
                  maxWidth: '80%', textAlign: msg.sender_type === 'vendor' ? 'right' : 'left'
                }}>
                  {msg.sender_type !== 'vendor' && <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>{msg.sender_name || 'Buyer'}</div>}
                  <div style={{
                    padding: '10px 16px', borderRadius: 18, display: 'inline-block',
                    background: msg.sender_type === 'vendor' ? 'var(--primary)' : 'var(--gray-100)',
                    color: msg.sender_type === 'vendor' ? '#fff' : 'var(--gray-800)'
                  }}>
                    {msg.content}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                    {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
              ))}
              <div ref={msgEndRef} />
            </div>
            <form onSubmit={sendMessage} style={{ display: 'flex', padding: 16, gap: 10, borderTop: '1px solid var(--gray-200)' }}>
              <input
                value={newMessage} onChange={(e) => setNewMessage(e.target.value)} placeholder="Type your message..."
                style={{ flex: 1, padding: '12px 18px', borderRadius: 24, border: '1px solid var(--gray-200)', outline: 'none', fontSize: 14 }}
              />
              <button type="submit" style={{
                background: 'var(--primary)', color: '#fff', border: 'none', padding: '12px 24px',
                borderRadius: 24, cursor: 'pointer', fontWeight: 600, fontSize: 14
              }} disabled={!newMessage.trim()}>Send</button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
